import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Typography, Button, Spin, message } from 'antd';
import { 
  UserOutlined, 
  SettingOutlined, 
  ToolOutlined, 
  BookOutlined,
  EyeOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getManualsList } from '../../services/manualService';
import { showLoadError } from '../common/ErrorMessage';
import { useLoading } from '../../hooks/useLoading';

const { Title, Text } = Typography;

interface Manual {
  id: string;
  title: string;
  description: string;
  filename: string;
  icon: string;
}

const ManualList: React.FC = () => {
  const [manuals, setManuals] = useState<Manual[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading, setLoading } = useLoading(true);
  const { t } = useTranslation();

  const fetchManuals = useCallback(async () => {
    try {
      setLoading(true);
      // 로그인하지 않은 사용자는 역할을 전달하지 않음 (undefined)
      const userRole = user?.role || undefined;
      const data = await getManualsList(userRole);
      setManuals(data);
    } catch (error) {
      showLoadError(t('manuals.list'));
    } finally {
      setLoading(false);
    }
  }, [t, setLoading, user?.role]);

  useEffect(() => {
    fetchManuals();
  }, [fetchManuals]);

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'user':
        return <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />;
      case 'setting':
        return <SettingOutlined style={{ fontSize: '24px', color: '#52c41a' }} />;
      case 'tool':
        return <ToolOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />;
      default:
        return <BookOutlined style={{ fontSize: '24px', color: '#722ed1' }} />;
    }
  };

  const handleViewManual = (manualId: string) => {
    navigate(`/manuals/${manualId}`);
  };

  const handleDownloadManual = async (manualId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://localhost:${process.env.REACT_APP_BACKEND_PORT || 3000}`}/api/manuals/${manualId}`);
      const content = await response.text();
      
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${manualId}_manual.md`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success(t('manuals.downloadSuccess'));
    } catch (error) {
      message.error(t('manuals.downloadFailed'));
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('manuals.loading')}</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <BookOutlined style={{ marginRight: '8px' }} />
          {t('manuals.title')}
        </Title>
        <Text type="secondary">
          {t('manuals.description')}
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        {manuals.map((manual) => (
          <Col xs={24} sm={12} lg={8} key={manual.id}>
            <Card
              hoverable
              style={{ height: '100%' }}
              actions={[
                <Button 
                  type="link" 
                  icon={<EyeOutlined />}
                  onClick={() => handleViewManual(manual.id)}
                >
                  {t('manuals.actions.view')}
                </Button>,
                <Button 
                  type="link" 
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadManual(manual.id)}
                >
                  {t('manuals.actions.download')}
                </Button>
              ]}
            >
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                {getIcon(manual.icon)}
              </div>
              <Card.Meta
                title={manual.title}
                description={
                  <div>
                    <Text type="secondary">{manual.description}</Text>
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>

      <div style={{ marginTop: '32px', padding: '24px', background: '#f5f5f5', borderRadius: '8px' }}>
        <Title level={4}>{t('manuals.guide.title')}</Title>
        <ul>
          <li><strong>{t('manuals.guide.userManual')}</strong>: {t('manuals.guide.userManualDesc')}</li>
          {user?.role === 'ADMIN' && (
            <>
              <li><strong>{t('manuals.guide.adminManual')}</strong>: {t('manuals.guide.adminManualDesc')}</li>
              <li><strong>{t('manuals.guide.deploymentGuide')}</strong>: {t('manuals.guide.deploymentGuideDesc')}</li>
            </>
          )}
        </ul>
        <Text type="secondary">
          {t('manuals.guide.offlineNote')}
          {user?.role !== 'ADMIN' && (
            <span style={{ display: 'block', marginTop: '8px', color: '#fa8c16' }}>
              {t('manuals.guide.adminOnlyNote')}
            </span>
          )}
        </Text>
      </div>
    </div>
  );
};

export default ManualList; 